import React, { useEffect, useMemo, useState } from "react";
import { Tree, Radio, Modal, Typography, Flex, message } from "antd";
import type { DataNode } from "antd/es/tree";
import { BooleanReturnType } from "hooks/use-boolean";
import { FolderOutlined } from "@ant-design/icons";
import { get, isEmpty, isEqual } from "lodash";

interface Props {
  dialogBool: BooleanReturnType;
  moveToFolder: (folderId: string) => void;
  onCancel: () => void;
  schedules: any[];
  loading: boolean;
}

const MoveFolderDialog = ({ schedules, loading, dialogBool, moveToFolder, onCancel }: Props) => {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const folders: DataNode[] = useMemo(() => {
    const rec = (parent: any, children: any[]) => {
      parent.children = [];
      children
        .filter((item) => item?.folder && !isEqual(item.id, get(dialogBool, "data.id")))
        .forEach((child, ind) => {
          parent.children[ind] = { key: child.id, title: child.name };
          if (!isEmpty(child.children)) rec(parent.children[ind], child.children);
        });
    };

    const res: any = {};
    rec(res, schedules);
    return res.children;
  }, [dialogBool, schedules]);

  const onFinish = () => {
    if (!selectedKey) return message.error("Please select a folder!");
    moveToFolder(selectedKey);
  };

  const handleSelect = (keys: React.Key[]) => {
    const [key] = keys;
    setSelectedKey(key as string);
  };

  const renderTreeNodeTitle = (node: DataNode) => (
    <Radio checked={node.key === selectedKey} onChange={() => setSelectedKey(node.key as string)}>
      <Flex align="center" gap={5}>
        <FolderOutlined />
        <Typography>{node.title as string}</Typography>
      </Flex>
    </Radio>
  );

  useEffect(() => {
    if (!dialogBool.value) {
      setSelectedKey(null);
    }
  }, [dialogBool]);

  return (
    <Modal onOk={onFinish} onCancel={onCancel} okButtonProps={{ loading }} okText="Move" title={"Select a folder to move"} open={dialogBool.value}>
      <Tree
        treeData={folders}
        defaultExpandAll
        onSelect={handleSelect}
        selectable={false} // Disable built-in selection to rely on radio
        titleRender={renderTreeNodeTitle}
      />
    </Modal>
  );
};
export default MoveFolderDialog;
